import React, { useState } from 'react';

import cx from 'classnames';
import { flatten, isEmpty } from 'ramda';
import { useDispatch } from 'react-redux';
import { useNetworkState } from 'react-use';

import RenderInDomNode from 'components/Pages/Admin/Page/common/RenderInStuff';
import Button from 'components/shared/Button';
import CheckBox from 'components/shared/FormAdapters/FormDefault/CheckBox';
import Icon from 'components/shared/Icon';
import Download2 from 'components/shared/Icon/icons/Download2';
import Visibility from 'components/shared/Icon/icons/Visibility';
import Tooltip from 'components/shared/Tooltip';
import Link from 'components/shared/Typography/Link';
import Text from 'components/shared/Typography/Text';
import { useSnackbar } from 'hooks/useSnackbar';
import i18n from 'i18n';
import { viewThunk } from 'store/assets/thunks';
import { ASSET_STORAGE_TYPES } from 'utils/asset';
import { baseApiURL } from 'utils/axios';
import { saveAsZIP } from 'utils/filesDownloaders/zip';
import { LANDING_THEMES } from 'utils/itineraries/functions';
import { checkIsWebview } from 'utils/mobileApplication';
import { isDefined, isNotDefined } from 'utils/ramda';

import styles from './index.module.css';

const useHandlers = ({
  sections,
  getCreateArchiveEndpoint,
  getPingEndpoint,
  getDownloadEndpoint,
  archiveName = 'files',
}) => {
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const onSnackbar = useSnackbar();

  const filesLength = sections
    .filter((section) => section.files)
    .reduce((acc, section) => acc + section.files.length, 0);
  const [idsMap, setIdsMap] = useState(new Map());
  const isAllChecked = idsMap.size === filesLength;
  const dispatch = useDispatch();

  const handleToggleFile = (id) => {
    if (idsMap.get(id)) {
      idsMap.delete(id);
      setIdsMap(new Map(idsMap));
      return;
    }

    return setIdsMap(new Map(idsMap.set(id, true)));
  };

  const handleCheckAll = () => {
    if (isAllChecked) {
      setIdsMap(new Map());
    } else {
      setIdsMap(
        new Map(
          sections
            .filter((section) => section.files)
            .map((section) => section.files.map((file) => [file.id, true]))
            .flat(),
        ),
      );
    }
  };

  const loadFiles = async (ids, translate = false) => {
    try {
      setIsLoadingFiles(true);
      await saveAsZIP({
        title: archiveName,
        data: { documentIds: ids },
        urls: {
          create: () => `${baseApiURL}${getCreateArchiveEndpoint()}`,
          ping: (taskId) => `${baseApiURL}${getPingEndpoint(taskId)}`,
          download: (taskId) => `${baseApiURL}${getDownloadEndpoint(taskId)}`,
        },
        translate,
      });
    } catch (err) {
      onSnackbar("Couldn't download files. Please try again later.", true);
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const handleLoadFiles = (translate) => () => {
    if (isLoadingFiles) return;

    loadFiles([...idsMap.keys()], translate);
  };
  const handleLoadFile = (translate) => async (fileId) => {
    if (isLoadingFiles) return;

    loadFiles([fileId], translate);
  };

  const handleOpenFilePreview = async (fileId) => {
    await dispatch(viewThunk({ id: fileId, storageType: ASSET_STORAGE_TYPES.DOCUMENTS, options: { hasTags: false } }));
  };

  return {
    handleToggleFile,
    handleCheckAll,
    isAllChecked,
    isNoneChecked: idsMap.size === 0,
    isFileChecked: (id) => Boolean(idsMap.get(id)),
    handleLoadFiles,
    handleOpenFilePreview,
    handleLoadFile,
    isLoadingFiles,
  };
};

function GuestFileManagerInner({
  title,
  titleMuted,
  embed = false,
  embedNodeId,
  groupedSections,
  onOpenPreview,
  archiveName,
  getCreateArchiveEndpoint,
  getPingEndpoint,
  getDownloadEndpoint,
  theme = LANDING_THEMES.default,
  disabled,
  translate,
}) {
  const sections = flatten(groupedSections.map((group) => group.sections));

  const {
    handleCheckAll,
    handleToggleFile,
    isAllChecked,
    isNoneChecked,
    isFileChecked,
    handleOpenFilePreview,
    handleLoadFile,
    handleLoadFiles,
    isLoadingFiles,
  } = useHandlers({ sections, getCreateArchiveEndpoint, getPingEndpoint, getDownloadEndpoint, archiveName });
  const { online } = useNetworkState();
  const isWebview = checkIsWebview();
  const isMultiLoadingAvailable = online && !isWebview;

  const selectAll = (
    <div
      className={cx(
        styles.selectAll,
        'custom-guest-file-manager-select-all',
        theme !== LANDING_THEMES.default && styles.selectAllNomad,
      )}
    >
      <CheckBox
        type="primary"
        size="sm"
        centered
        noMargin
        className={cx(styles.selectAllCheckbox, 'custom-guest-file-manager-select-all-checkbox')}
        onChange={handleCheckAll}
        checked={isAllChecked}
      >
        <Text className={cx(styles.selectAllText, 'custom-guest-file-manager-select-all-text')}>
          {i18n.t('proposal_content.guest_portal.travel_wallet.actions.select_all', { defaultValue: 'Select All' })}
        </Text>
      </CheckBox>
      <Button
        disabled={isLoadingFiles || isNoneChecked}
        className={cx(styles.downloadFileBtn, 'custom-guest-file-manager-download-file-btn')}
        size="extraSmall"
        onClick={handleLoadFiles(translate)}
      >
        {isLoadingFiles
          ? i18n.t('proposal_content.guest_portal.travel_wallet.loading', {
              defaultValue: 'Loading...',
            })
          : i18n.t('proposal_content.guest_portal.travel_wallet.actions.download_files', {
              defaultValue: 'Download Files',
            })}
      </Button>
    </div>
  );

  return (
    <div
      className={cx('custom-guest-file-manager-sections', theme !== LANDING_THEMES.default && styles.containerNomad)}
    >
      {!embed && (
        <div className={cx(styles.header, 'custom-guest-file-manager-header')}>
          <div
            className={cx(
              styles.headerTitle,
              titleMuted && styles.headerTitleMuted,
              'custom-guest-file-manager-header-title',
              titleMuted && 'custom-guest-file-manager-header-title-muted',
            )}
          >
            {title}
          </div>
          {(isMultiLoadingAvailable && selectAll) || null}
        </div>
      )}

      {embed && embedNodeId && (
        <RenderInDomNode id={embedNodeId}>{(isMultiLoadingAvailable && selectAll) || null}</RenderInDomNode>
      )}

      {groupedSections.map((group) => (
        <div key={group.title} className={cx(styles.sections, 'custom-guest-file-manager-sections')}>
          <div className={styles.groupSectionTitle}>{group.title}</div>

          {group.sections.map((section) => (
            <div key={section.title} className={cx(styles.section, 'custom-guest-file-manager-section')}>
              <div className={cx(styles.sectionTitle, 'custom-guest-file-manager-section-title')}>{section.title}</div>

              <div className={cx(styles.items, 'custom-guest-file-manager-items')}>
                {isDefined(section.files) &&
                  section.files.map((file) => {
                    return (
                      <FileItem
                        disabled={disabled}
                        key={file.id}
                        file={file}
                        onOpenPreview={onOpenPreview || handleOpenFilePreview}
                        onLoadFile={isWebview ? handleLoadFile(translate) : online && handleLoadFile(translate)}
                        checked={isFileChecked(file.id)}
                        onToggleChecked={isMultiLoadingAvailable && handleToggleFile}
                      />
                    );
                  })}

                {isDefined(section.links) &&
                  section.links.map((linkData) => {
                    return <LinkItem key={linkData.id} linkData={linkData} />;
                  })}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function FileItem({ file, onOpenPreview, onLoadFile, checked, onToggleChecked, disabled }) {
  return (
    <div className={cx(styles.item, 'custom-guest-file-manager-item')}>
      <div className={cx(styles.itemName, 'custom-guest-file-manager-item-name')}>{file.name}</div>
      <div className={cx(styles.fileActions, 'custom-guest-file-manager-file-actions')}>
        <button
          type="button"
          className={styles.fileBtn}
          data-document-action="preview"
          data-document-id={file.id}
          data-document-type={file.mimeType}
          data-document-file={file.name}
          data-document-uri={file.previewUrl}
          onClick={() => {
            onOpenPreview(file.id);
          }}
        >
          <Icon
            disabled={disabled}
            className={cx(styles.eye, 'custom-guest-file-manager-eye-icon')}
            component={Visibility}
            data-tip
            data-for={`gp_filemanager_preview_btn_${file.id}`}
          />
          <Tooltip id={`gp_filemanager_preview_btn_${file.id}`}>
            {i18n.t('proposal_content.guest_portal.travel_wallet.actions.see_preview', {
              defaultValue: 'See the file preview',
            })}
          </Tooltip>
        </button>

        {onLoadFile && (
          <button
            type="button"
            className={styles.fileBtn}
            data-document-action="download"
            data-document-id={file.id}
            data-document-type={file.mimeType}
            data-document-file={file.name}
            data-document-uri={file.url}
            onClick={() => onLoadFile(file.id)}
          >
            <Icon
              disabled={disabled}
              className={cx(styles.download, 'custom-guest-file-manager-download-icon')}
              component={Download2}
              data-tip
              data-for={`gp_filemanager_download_btn_${file.id}`}
            />
            <Tooltip id={`gp_filemanager_download_btn_${file.id}`}>
              {i18n.t('proposal_content.guest_portal.travel_wallet.actions.download_single', {
                defaultValue: 'Download the single file',
              })}
            </Tooltip>
          </button>
        )}

        {onToggleChecked && (
          <CheckBox
            type="primary"
            disabled={disabled}
            noMargin
            size="sm"
            onChange={() => onToggleChecked(file.id)}
            checked={checked}
          />
        )}
      </div>
    </div>
  );
}

function LinkItem({ linkData }) {
  return (
    <div className={cx(styles.item, 'custom-guest-file-manager-item')}>
      <Link
        href={linkData.link}
        className={cx(styles.itemName, 'custom-guest-file-manager-item-name')}
        target="_blank"
        rel="noopener noreferrer"
        data-external-link=""
      >
        {linkData.name}
      </Link>
    </div>
  );
}

export default function GuestFileManager({ data = [], ...rest }) {
  const arr = Array.isArray(data) ? data : [data];
  const filteredGroupedSections =
    arr
      .map((group) => {
        const filteredSections = group.sections?.filter(
          (section) => isDefined(section.files) || isDefined(section.links),
        );
        return { ...group, sections: filteredSections };
      })
      .filter((group) => {
        return !isEmpty(group.sections);
      }) || [];

  if (isNotDefined(filteredGroupedSections)) {
    return null;
  }

  return (
    <GuestFileManagerInner
      title={data.title}
      titleMuted={data.titleMuted}
      groupedSections={filteredGroupedSections}
      {...rest}
    />
  );
}
